import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { TextInput, Image } from "hesus-ui-kit";
import styled from "styled-components";
import PulseLoader from "react-spinners/PulseLoader";
import removalIcon from "@icons/ic-removal-product.svg";
import supplyIcon from "@icons/ic-supply-product.svg";
import truckIcon from "@icons/ic-truck.svg";
import enableIcon from "@icons/ic-enable.svg";
import disableIcon from "@icons/ic-disable.svg";
import CenterProductPrices from "./components/CenterProductPrices/CenterProductPrices";
import { CENTER_PRODUCTS_LIST_ENDPOINT } from "@utils/constants";
import { loadAllCenterProducts } from "./CenterProductsList.actionCreators";
import { setAllCenterProductsLoadingState } from "./CenterProductsList.slice";
import PaginationData from "@shared/baseUI/PaginationData/PaginationData";
import CenterProductAction from "./components/CenterProductAction/CenterProductAction";
import { UndefinedMessageError, searchInputStyles } from "@utils/extra-style";
import {
  OuterWrapper,
  FlexRow,
  FilterFlexRow,
  SectionContainer,
  GridRow,
  ProductContainer,
  CategoryDetail,
  ButtonWrapper,
  ButtonData,
  CreateButton,
  FilterOuter,
  GlobalSearch,
  GridLoaderItem,
} from "./CenterProductsList.styled";

import "./CenterProductsList.scss";
import { textReplace } from "@utils/utils";

const ViewData = (props) => {
  const { t, centerId, companyId } = props;

  const [activeStatus, setActiveStatus] = useState(1);
  const [removalStatus, setRemovalStatus] = useState(1);
  const [supplyStatus, setSupplyStatus] = useState(0);
  const [data, setData] = useState([]);
  const [isRefreshDataAgain, setRefreshState] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [requestData, setRequestData] = useState({});

  const [currentPage, setCurrentPage] = useState(1);
  const [pageStart, setPageStart] = useState(0);
  const [pageSize, setPageSize] = useState(9);
  const [search, setSearch] = useState("");
  const [sort, setSortOption] = useState({ dir: "asc", column: "id" });

  const history = useNavigate();

  const onProductCreateClick = () => {
    history(`/company/view/${companyId}/center/${centerId}/product/create`);
  };

  let dataURL = textReplace(
    CENTER_PRODUCTS_LIST_ENDPOINT,
    "{companyId}",
    companyId
  );
  dataURL = textReplace(dataURL, "{centreId}", centerId);

  const dispatch = useDispatch();

  const centerProductsListByIds = useSelector(
    (state) => state.centerProductsList.byId
  );
  const centerProductsListAllIds = useSelector(
    (state) => state.centerProductsList.allIds
  );
  const filteredRecords = useSelector(
    (state) => state.centerProductsList.filteredRecords
  );

  const isCenterProductsListLoading = useSelector(
    (state) => state.centerProductsList.isLoading
  );

  useEffect(() => {
    setLoading(isCenterProductsListLoading);
  }, [isCenterProductsListLoading]);

  /**
   * Method to fetch set of records based on the filters applied.
   * @param {pageSize} pageSize
   * @param {pageStart} pageStart
   * @param {globalFilter} globalFilter
   * @param {sort} sort
   * @param {appliedFilters} appliedFilters
   */
  const fetchRecords = ({
    pageSize,
    pageStart,
    search,
    sort,
    appliedFilters,
    currentPage,
  }) => {
    // save request data,
    setRequestData({ pageSize, pageStart, search, sort, appliedFilters });
    // Set the loading state
    setLoading(true);

    dispatch(setAllCenterProductsLoadingState());
    dispatch(
      loadAllCenterProducts({
        dataURL,
        pageSize,
        currentPage,
        sort,
        search,
        appliedFilters,
        status: 1,
      })
    );
  };

  useEffect(() => {
    if (isRefreshDataAgain) {
      setRefreshState(false);
    }
  }, [isRefreshDataAgain]);

  useEffect(() => {
    if (!loading) {
      fetchRecords({
        pageSize,
        pageStart,
        search,
        sort,
        appliedFilters: {
          status: activeStatus,
          removal: removalStatus,
          supply: supplyStatus,
        },
        currentPage,
      });
    }
  }, [
    currentPage,
    pageSize,
    pageStart,
    activeStatus,
    removalStatus,
    supplyStatus,
    search,
    isRefreshDataAgain,
  ]);

  useEffect(() => {
    if (centerProductsListAllIds.length > 0) {
      const responseData = centerProductsListAllIds.map((id) => {
        const tempData = { ...centerProductsListByIds[id] };
        return tempData;
      });
      setData([...responseData]);
      setPageCount(filteredRecords);
      setLoading(false);
    } else {
      setData([]);
      setPageCount(0);
      setLoading(false);
    }
  }, [t, centerProductsListAllIds, centerProductsListByIds, filteredRecords]);

  const changeStatus = (label) => {
    if (label === "status") {
      const newStatus = activeStatus === 1 ? 0 : 1;
      setActiveStatus(newStatus);
      setSortOption(
        newStatus
          ? { dir: "desc", column: "status" }
          : { dir: "desc", column: "id" }
      );
    } else if (label === "removal") {
      const newStatus = removalStatus === 1 ? 0 : 1;
      setSortOption(
        newStatus
          ? { dir: "asc", column: "serviceType" }
          : { dir: "desc", column: "id" }
      );
      setRemovalStatus(newStatus);
    } else if (label === "supply") {
      const newStatus = supplyStatus === 1 ? 0 : 1;
      setSupplyStatus(newStatus);
    }
    setCurrentPage(1);
  };

  const globalSearchFilter = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const getFilterPrice = (prices) => {
    if (prices !== null) {
      return prices.filter((price) => price.isActive === 1);
    }
    return prices;
  };

  return (
    <>
      <OuterWrapper>
        <FlexRow>
          <SectionContainer>
            {centerId === undefined && (
              <UndefinedMessageError>
                {t("COMPANY_PRODUCTS_MISSING_CENTER_IN_DB")}
              </UndefinedMessageError>
            )}
            {loading && (
              <GridRow column="1fr">
                <GridLoaderItem>
                  <PulseLoader width="100%" color="#1bace4" />
                </GridLoaderItem>
              </GridRow>
            )}
            {!loading && centerId !== undefined && (
              <>
                <div>
                  <FilterFlexRow>
                    <FilterOuter>
                      <div className="status__filters-wrapper">
                        <Image
                          src={activeStatus === 1 ? enableIcon : disableIcon}
                          alt={t("PRODUCTS_FILTER_ACTIVE_ONLY")}
                          title={t("PRODUCTS_FILTER_ACTIVE_ONLY")}
                          classname="center-products-list__image-icon-margin"
                          onClick={() => changeStatus("status")}
                        />
                        <label className="label center-products-list__active-label">
                          {t("PRODUCTS_FILTER_ACTIVE_ONLY")}
                        </label>
                      </div>
                      <div className="status__filters-wrapper">
                        <Image
                          src={removalStatus === 1 ? enableIcon : disableIcon}
                          alt={t("PRODUCTS_FILTER_REMOVAL")}
                          title={t("PRODUCTS_FILTER_REMOVAL")}
                          classname="center-products-list__image-icon-margin"
                          onClick={() => changeStatus("removal")}
                        />
                        <label className="label center-products-list__active-label">
                          {t("PRODUCTS_FILTER_REMOVAL")}
                        </label>
                      </div>
                      <div className="status__filters-wrapper">
                        <Image
                          src={supplyStatus === 1 ? enableIcon : disableIcon}
                          alt={t("PRODUCTS_FILTER_SUPPLY")}
                          title={t("PRODUCTS_FILTER_SUPPLY")}
                          classname="center-products-list__image-icon-margin"
                          onClick={() => changeStatus("supply")}
                        />
                        <label className="label center-products-list__active-label">
                          {t("PRODUCTS_FILTER_SUPPLY")}
                        </label>
                      </div>
                    </FilterOuter>
                    <GlobalSearch>
                      <div className="search-box">
                        <TextInput
                          id="center-product-list-search"
                          type="search"
                          inputClass="search-box-default-input"
                          value={search || ""}
                          onChange={(e) => globalSearchFilter(e)}
                          placeholder=""
                          name="global-filter"
                          inputStyles={searchInputStyles}
                        />
                      </div>
                    </GlobalSearch>
                    <ButtonWrapper>
                      <CreateButton onClick={onProductCreateClick}>{`${t(
                        "ACTION_NEW_PRODUCT"
                      )}`}</CreateButton>
                    </ButtonWrapper>
                  </FilterFlexRow>
                </div>
                <div>
                  <GridRow column="repeat(auto-fit, minmax(400px, 520px));">
                    {data.length !== "" &&
                      data.map((data, index) => {
                        return (
                          <ProductContainer key={index}>
                            <GridRow column="10% 55% auto" gap="0rem">
                              <Image
                                src={
                                  data.serviceType === "IN"
                                    ? removalIcon
                                    : supplyIcon
                                }
                                title={t(
                                  data.serviceType === "IN"
                                    ? "PRODUCTS_FILTER_REMOVAL"
                                    : "PRODUCTS_FILTER_SUPPLY"
                                )}
                                alt="Avatar"
                                classname="center-products-list__image-icon"
                                opacity={!data.isActive ? ".2" : 1}
                              />

                              <span>
                                <Link
                                  to={`/company/view/${companyId}/center/${centerId}/product/view/${data.id}`}
                                  className="center-products-list__product-link"
                                  style={{
                                    opacity: !data.isActive ? ".2" : "1",
                                  }}
                                >
                                  {data?.product?.name +
                                    (data.label ? " - " + data.label : "")}
                                </Link>
                              </span>
                              <CenterProductAction
                                data={data}
                                centerId={centerId}
                                companyId={companyId}
                                onRefreshData={setRefreshState}
                              />
                            </GridRow>
                            <GridRow
                              column="1fr 1fr"
                              opacity={!data.isActive ? ".2" : 1}
                            >
                              <CategoryDetail>
                                {t(data?.product?.category.name) +
                                  "-" +
                                  t(data?.product?.subCategory.name)}
                              </CategoryDetail>
                              <ButtonWrapper>
                                {data.logisticFlow === 1 && (
                                  <ButtonData colour="#EFCA06">{`${t(
                                    "CENTER_PRODUCT_LOGISTIC_FLOW_ROUND_TRIP"
                                  )}`}</ButtonData>
                                )}
                                {data.logisticFlow === 2 && (
                                  <ButtonData colour="#6ACFCB">{`${t(
                                    "LOGISTIC_FLOW_RETURN_LOAD"
                                  )}`}</ButtonData>
                                )}
                              </ButtonWrapper>
                            </GridRow>

                            <GridRow
                              column="30px auto"
                              opacity={!data.isActive ? ".2" : 1}
                            >
                              {data.logisticFlow === 2 && (
                                <>
                                  <Image
                                    src={truckIcon}
                                    alt="truck"
                                    classname="center-products-list__image-icon-margin"
                                  />
                                  <span>
                                    <Anchor
                                      textDecoration="none"
                                      className="center-products-list__product-link"
                                      styles={{
                                        textDecoration: "none !importnat",
                                      }}
                                    >
                                      {data?.haulier?.companyName}
                                    </Anchor>
                                  </span>
                                </>
                              )}
                              {data.logisticFlow !== 0 && (
                                <p style={{ margin: "0 0 2px" }}>&nbsp;</p>
                              )}
                            </GridRow>
                            <hr />
                            <CenterProductPrices
                              prices={getFilterPrice(data.prices)}
                              status={data.isActive}
                            />
                          </ProductContainer>
                        );
                      })}
                  </GridRow>
                  {(data.length === "" || data.length === 0) && (
                    <GridRow column="1fr">{t("NO_CENTRE_PRODUCTS")}</GridRow>
                  )}
                </div>
              </>
            )}
          </SectionContainer>
        </FlexRow>

        {data.length !== 0 && (
          <PaginationData
            pageCount={parseInt(pageCount, 10)}
            pageSize={requestData.pageSize}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            pageStart={pageStart}
            setPageStart={setPageStart}
            setPageSize={setPageSize}
          />
        )}
      </OuterWrapper>
    </>
  );
};

ViewData.propTypes = {
  t: PropTypes.func.isRequired,
};

export default withTranslation()(ViewData);
